import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  LanguageSkill,
  LanguageSkillFilters,
  languageSkillsQuery,
} from "./languageSkillsApi";

const arrayAdapter = createEntityAdapter<LanguageSkill>();

export const fetchLanguageSkills = createAsyncThunk<
  Array<LanguageSkill>,
  LanguageSkillFilters,
  { state: RootState }
>(
  "projections/languageSkills",
  async (filters: LanguageSkillFilters, { requestId }) =>
    await languageSkillsQuery(filters, { requestId }),
);

type Entities = {
  ids: [];
  entities: Record<string, any>;
};

type CodeNameIndex = Record<string, string>;

export type SliceState = {
  array: Entities;
  codeNameIndex: CodeNameIndex;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

export const languageSkillsSlice = createSlice({
  name: "languageSkills",
  initialState: {
    array: arrayAdapter.getInitialState(),
    codeNameIndex: {},
    status: "idle",
  } as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchLanguageSkills.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchLanguageSkills.fulfilled, (state, action) => {
      const array = action.payload as Array<LanguageSkill>;
      arrayAdapter.setAll(state.array, array);

      const codeNameIndex = array.reduce((acc, languageSkill) => {
        acc[languageSkill.code] = languageSkill.name;
        return acc;
      }, {} as CodeNameIndex);
      state.codeNameIndex = codeNameIndex;

      state.status = "succeeded";
    });
    builder.addCase(fetchLanguageSkills.rejected, (state, action) => {
      state.error = action.error.message;
      state.status = "failed";
    });
  },
});

export const { selectAll: selectLanguageSkillsArray } =
  arrayAdapter.getSelectors((state: RootState) => state.languageSkills.array);

export const selectLanguageSkillsCodeNameIndex = (state: RootState) =>
  state.languageSkills.codeNameIndex;

export default languageSkillsSlice.reducer;
